import { useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Grid, Hidden, IconButton } from "@material-ui/core";
import messages from "./messages";
import { makeStyles } from "@material-ui/core/styles";
import { FormattedMessage } from "react-intl";
import Edit from "images/EditIcon";
import Delete from "images/DeleteIcon";
import moment from "moment";
import { hasRole, ROLES } from "utils/userStorage";
import {DEFAULT_LOCALE} from 'i18n.js'
import RefrezhButton from "components/RefrezhButton";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import history from "utils/history";

const useStyles = makeStyles(theme => ({
  header: {
    fontWeight: "bold",
    padding: theme.spacing(1),
  },
  info: {
    color: ({ isCurrent }) => (isCurrent ? "inherit" : "#a0a0a0"),
    padding: theme.spacing(1),
  },
  treatmentWrapper: {
    "&:not(:last-child)": {
      borderBottom: "1px solid #454546",
    },

    padding: theme.spacing(2),
  },
  inactiveProductButton: {
    textDecoration: "underline",
    textTransform: "uppercase",
  },
  actionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  underlinedButton: {
    textDecoration: "underline",
    textTransform: "uppercase",

    "&:hover": {
      // backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
  action: {},
}));

const PROCESSED = 4;

function ProductRow({
  product,
  index,
  onClickInactive,
  isCurrent = false,
  disabled = false,
  canPrevious = true,
  onSkip,
  onPrevious,
  canSkip = true,
  onEdit,
  onDeleteButtonClick,
}) {

  let params = useParams()

  const classes = useStyles({
    isCurrent,
  });

  const inactiveName = useMemo(
    () => {
      let name = product.lowerProductName;

      if (name.length < 2) {
        name = product.upperProductName;
      }

      if (name.length < 2) {
        name = "";
      }

      return name;
    },
    [product.upperProductName, product.lowerProductName],
  );

  // Product is optional
  if (!product.active) {
    return (
      <Grid
        justify="flex-start"
        key={product.id}
        className={classes.treatmentWrapper}
        container
      >
        <Button
          disabled={disabled}
          onClick={onClickInactive}
          className={classes.inactiveProductButton}
          variant="text"
        >
          {(() => {
            if(DEFAULT_LOCALE == 'nl'){
              return "plan extra try-in behandeling"
            }else{
              return "plan additional try-in treatment"
            }
          })()}
          
          {/* <FormattedMessage {...messages.planExtraItem} /> */}
        </Button>
      </Grid>
    );
  }

  return (
    <Grid key={product.id} className={classes.treatmentWrapper} container>
      <Grid item xs={12} sm={4} md={3}>
        <Hidden mdUp={index > 0}>
          <div className={classes.header}>
            <FormattedMessage {...messages.treatmentPhase} />
          </div>
        </Hidden>
        <div className={classes.info} data-cy="infoTreatment">
          {`${index + 1}. ${product.fase}`}
          {(product.deliveryDate > 0
            ? moment.unix(product.deliveryDate).isAfter(moment(), "day")
            : product.status > 1) &&
            (hasRole([ROLES.Admin, ROLES.CS]) ||
              product.status !== PROCESSED) && (
              <div className={classes.actions}>
                <IconButton
                  data-cy="editButton"
                  onClick={() => onEdit(product.id)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  data-cy="deleteButton"
                  onClick={() => {
                    onDeleteButtonClick(product.id);
                  }}
                >
                  <Delete />
                </IconButton>
              </div>
            )}
        </div>
      </Grid>
      <Grid item container xs={12} sm={8} md={9}>
        <Grid item xs={12} sm={6} md={4}>
          <Hidden mdUp={index > 0}>
            <div className={classes.header}>
              <FormattedMessage {...messages.expectedDeliveryDate} />
            </div>
          </Hidden>
          <div className={classes.info} data-cy="expectedDate">
            {product.deliveryDate ? (
              moment.unix(product.deliveryDate).format("DD-MM-YYYY")
            ) : (
              <RefrezhButton onClick={() => {
                window.dispatchEvent(new CustomEvent('orderproduct',{detail:{index}}))
                // history.push(`/order/patient/${params.patientId}/treatment/${product.id}`)
              }}>Product bestellen</RefrezhButton>
              // <FormattedMessage {...messages.unknown} />
            )}
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Hidden mdUp={index > 0}>
            <div className={classes.header}>
              <FormattedMessage {...messages.productUpperJaw} />
            </div>
          </Hidden>
          <div className={classes.info}>{product.upperProductName}</div>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Hidden mdUp={index > 0}>
            <div className={classes.header}>
              <FormattedMessage {...messages.productLowerJaw} />
            </div>
          </Hidden>
          <div className={classes.info}>{product.lowerProductName}</div>
        </Grid>
      </Grid>
      {isCurrent && (
        <Grid container>
          <Grid
            item
            container
            justify="flex-start"
            xs={6}
            data-cy="prevButtonRow"
          >
            {canPrevious && (
              <Button
                disableRipple
                className={classes.underlinedButton}
                onClick={onPrevious}
                disabled={!canPrevious}
              >
                <FormattedMessage {...messages.previous} />
              </Button>
            )}
          </Grid>
          <Grid item container justify="flex-end" xs={6} data-cy="skipButton">
            {canSkip && (
              <Button
                disableRipple
                className={classes.underlinedButton}
                onClick={onSkip}
                disabled={!canSkip}
              >
                <FormattedMessage {...messages.skip} />
              </Button>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}

ProductRow.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    fase: PropTypes.string,
    order: PropTypes.number,
    lowerProductId: PropTypes.number,
    lowerProductName: PropTypes.string,
    upperProductId: PropTypes.number,
    upperProductName: PropTypes.string,
    active: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onClickInactive: PropTypes.func,
  isCurrent: PropTypes.bool,
  onSkip: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  canSkip: PropTypes.bool,
  onEdit: PropTypes.func,
  onDeleteButtonClick: PropTypes.func,
};

export default ProductRow;
